import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { TOKEN_KEY } from "../api";
import { toast } from "sonner";
import { Mail, ArrowRight, ArrowLeft, Loader2 } from "lucide-react";
import { Logo } from "../components/Logo";
import { useAuth } from "../AuthContext";

const EMERGENT_AUTH = "https://auth.emergentagent.com/";

export default function Login() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [step, setStep] = useState("choose"); // choose | email | otp
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [agree, setAgree] = useState(false);

  const handleGoogle = () => {
    if (!agree) { toast.error("Please accept the Privacy & Terms first"); return; }
    const redirect = `${window.location.origin}/auth/callback`;
    window.location.href = `${EMERGENT_AUTH}?redirect=${encodeURIComponent(redirect)}`;
  };

  const sendOTP = async (e) => {
    e?.preventDefault?.();
    if (step === "choose" && !agree) { toast.error("Please accept the Privacy & Terms"); return; }
    if (!email.includes("@")) { toast.error("Enter a valid email"); return; }
    setBusy(true);
    try {
      await api.post("/auth/otp/request", { email });
      toast.success("Code sent. Check your inbox.");
      setStep("otp");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send code");
    } finally { setBusy(false); }
  };

  const verifyOTP = async (e) => {
    e?.preventDefault?.();
    if (code.length < 4) { toast.error("Enter the 6-digit code"); return; }
    setBusy(true);
    try {
      const r = await api.post("/auth/otp/verify", { email, code });
      localStorage.setItem(TOKEN_KEY, r.data.token);
      await refresh();
      toast.success("Welcome to NeuroEQ");
      nav(r.data.needs_onboarding ? "/onboarding/parent" : "/home");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid code");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-md card-soft p-8 rise-in" data-testid="login-card">
        <div className="flex items-center justify-between mb-6">
          {step !== "choose" ? (
            <button onClick={() => setStep(step === "otp" ? "email" : "choose")}
                    className="p-2 rounded-full hover:bg-muted" data-testid="login-back">
              <ArrowLeft className="w-4 h-4" />
            </button>
          ) : <Logo size={102} />}
          <span className="text-xs text-muted-foreground tracking-widest uppercase">Sign in</span>
        </div>

        {step === "choose" && (
          <>
            <h1 className="font-display text-3xl mb-2">Welcome back</h1>
            <p className="text-muted-foreground mb-7">Choose how you'd like to sign in.</p>

            <button onClick={handleGoogle} className="btn-ghost w-full mb-3 justify-center" data-testid="login-google-btn">
              <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.1A6.6 6.6 0 0 1 5.5 12c0-.73.12-1.44.34-2.1V7.06H2.18A11 11 0 0 0 1 12c0 1.77.42 3.44 1.18 4.94l3.66-2.84z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z"/>
              </svg>
              Continue with Google
            </button>

            <button onClick={() => { if (!agree) { toast.error("Please accept the Privacy & Terms"); return; } setStep("email"); }}
                    className="btn-primary w-full justify-center" data-testid="login-email-btn">
              <Mail className="w-4 h-4" />
              Continue with email
            </button>

            <label className="flex items-start gap-3 mt-6 text-sm text-muted-foreground select-none cursor-pointer"
                   data-testid="login-tc-label">
              <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)}
                     className="mt-1 w-4 h-4 accent-[hsl(var(--neu-orange))]"
                     data-testid="login-tc-checkbox"/>
              <span>
                I agree to NeuroEQ's <Link to="/privacy" className="text-[hsl(var(--neu-orange))] underline" data-testid="login-privacy-link">Privacy &amp; Terms</Link>.
                We never store photos, videos or sell your data.
              </span>
            </label>

            <p className="text-xs text-muted-foreground text-center mt-5">
              No spam, no marketing. Just your Sunday session.
            </p>
          </>
        )}

        {step === "email" && (
          <form onSubmit={sendOTP}>
            <h1 className="font-display text-3xl mb-2">Your email</h1>
            <p className="text-muted-foreground mb-6">We'll send a 6-digit code.</p>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                   placeholder="you@example.com" className="field mb-5" data-testid="login-email-input" autoFocus />
            <button disabled={busy} className="btn-primary w-full" data-testid="login-send-otp-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send code"} <ArrowRight className="w-4 h-4"/>
            </button>
          </form>
        )}

        {step === "otp" && (
          <form onSubmit={verifyOTP}>
            <h1 className="font-display text-3xl mb-2">Enter the code</h1>
            <p className="text-muted-foreground mb-6">Sent to <b>{email}</b>. Code expires in 10 minutes.</p>
            <input inputMode="numeric" pattern="[0-9]*" maxLength={6} required value={code}
                   onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                   placeholder="123456"
                   className="field mb-2 tracking-[0.6em] text-center text-2xl font-display"
                   data-testid="login-otp-input" autoFocus />
            <button type="button" onClick={sendOTP} className="text-xs text-[hsl(var(--neu-orange))] mb-5"
                    data-testid="login-resend-btn">Resend code</button>
            <button disabled={busy} className="btn-primary w-full" data-testid="login-verify-otp-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify and continue"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
